import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Alert
} from '@mui/material';
import { PageTitle, ListLayoutPanel, DataGrid, AddButton, GridEditButton } from '../../../components/common';
import { useMarcaStore } from './store';
import { type MarcaListItem, type ModeloListItem } from './types/marca';
import { MarcaEditDialog, ModeloEditDialog } from './components';

const MarcasPage: React.FC = () => {
  const {
    marcas,
    modelos,
    selectedMarca,
    marcasLoading,
    modelosLoading,
    marcasError,
    modelosError,
    fetchMarcas,
    setSelectedMarca,
    clearMarcasError,
    clearModelosError
  } = useMarcaStore();

  const [marcaDialogOpen, setMarcaDialogOpen] = useState(false);
  const [modeloDialogOpen, setModeloDialogOpen] = useState(false);
  const [editingMarcaId, setEditingMarcaId] = useState<number | null>(null);
  const [editingModeloId, setEditingModeloId] = useState<number | null>(null);

  useEffect(() => {
    fetchMarcas();
  }, [fetchMarcas]);

  const handleMarcaSelect = (marca: MarcaListItem) => {
    setSelectedMarca(marca);
  };

  const handleEditMarca = (marca: MarcaListItem) => {
    setEditingMarcaId(marca.idMarca);
    setMarcaDialogOpen(true);
  };

  const handleEditModelo = (modelo: ModeloListItem) => {
    setEditingModeloId(modelo.idModelo);
    setModeloDialogOpen(true);
  };

  const handleNewMarca = () => {
    setEditingMarcaId(null);
    setMarcaDialogOpen(true);
  };

  const handleNewModelo = () => {
    setEditingModeloId(null);
    setModeloDialogOpen(true);
  };

  const marcasColumns = [
    {
      field: 'descripcion',
      headerName: 'Descripción',
      flex: 1
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 100,
      sortable: false,
      renderCell: (params: { row: MarcaListItem }) => (
        <GridEditButton
          onClick={() => handleEditMarca(params.row)}
          ariaLabel={`Editar marca ${params.row.descripcion}`}
        />
      )
    }
  ];

  const modelosColumns = [
    {
      field: 'descripcion',
      headerName: 'Descripción',
      flex: 1
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 100,
      sortable: false,
      renderCell: (params: { row: ModeloListItem }) => (
        <GridEditButton
          onClick={() => handleEditModelo(params.row)}
          ariaLabel={`Editar modelo ${params.row.descripcion}`}
        />
      )
    }
  ];

  return (
    <ListLayoutPanel>
      <PageTitle
        title="Marcas y Modelos"
        subtitle="Gestión de marcas y sus modelos asociados"
      />

      {marcasError && (
        <Alert severity="error" onClose={clearMarcasError} sx={{ mb: 2 }}>
          {marcasError}
        </Alert>
      )}

      {modelosError && (
        <Alert severity="error" onClose={clearModelosError} sx={{ mb: 2 }}>
          {modelosError}
        </Alert>
      )}

      {/* Split screen: Marcas on left, Modelos on right */}
      <Box sx={{ display: 'flex', gap: 3, width: '100%', height: 'calc(100vh - 200px)' }}>
        {/* Left side: Marcas - 50% width */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Marcas
            </Typography>
            <AddButton
              text="Nueva Marca"
              onClick={handleNewMarca}
            />
          </Box>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <DataGrid
              columns={marcasColumns}
              rows={marcas}
              height={400}
              pagination={true}
              pageSize={10}
              loading={marcasLoading}
              getRowId={(row: MarcaListItem) => row.idMarca}
              onRowClick={(params) => handleMarcaSelect(params.row)}
            />
          </Box>
        </Box>

        {/* Right side: Modelos - 50% width */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">
                Modelos
              </Typography>
              <AddButton
                text="Nuevo Modelo"
                onClick={handleNewModelo}
                disabled={!selectedMarca}
              />
            </Box>
            {selectedMarca && (
              <Typography variant="body2" color="text.secondary">
                Marca: {selectedMarca.descripcion}
              </Typography>
            )}
          </Box>
          {modelosError && (
            <Alert severity="error" onClose={clearModelosError} sx={{ mb: 2 }}>
              {modelosError}
            </Alert>
          )}
          <Box sx={{ flex: 1, minHeight: 0 }}>
            {!selectedMarca ? (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="100%"
                color="text.secondary"
              >
                <Typography>
                  Selecciona una marca para ver sus modelos
                </Typography>
              </Box>
            ) : (
              <DataGrid
                columns={modelosColumns}
                rows={modelos}
                height={400}
                pagination={true}
                pageSize={10}
                loading={modelosLoading}
                getRowId={(row: ModeloListItem) => row.idModelo}
              />
            )}
          </Box>
        </Box>
      </Box>

      <MarcaEditDialog
        open={marcaDialogOpen}
        marcaId={editingMarcaId}
        onClose={() => {
          setMarcaDialogOpen(false);
          setEditingMarcaId(null);
        }}
      />

      <ModeloEditDialog
        open={modeloDialogOpen}
        modeloId={editingModeloId}
        onClose={() => {
          setModeloDialogOpen(false);
          setEditingModeloId(null);
        }}
      />
    </ListLayoutPanel>
  );
};

export default MarcasPage;